import { useNavigate } from 'react-router-dom';
import { LogOut, Moon, Sun, UserCircle } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUIStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { useLogout } from '@/hooks/useAuth';

function getInitials(firstName?: string, lastName?: string, email?: string): string {
  const f = firstName?.trim()?.[0] ?? '';
  const l = lastName?.trim()?.[0] ?? '';
  const initials = `${f}${l}`.toUpperCase();
  if (initials) return initials;
  return (email?.trim()?.[0] ?? '?').toUpperCase();
}

/**
 * App-header user menu. Folds the account link, the light/dark theme
 * toggle, and sign-out into a single avatar dropdown so they no longer
 * occupy permanent vertical space in the sidebar. The avatar button is
 * a 44px touch target (a11y.mdc §2) with a visible focus ring.
 */
export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const logoutMutation = useLogout();
  const navigate = useNavigate();

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Account';
  const initials = getInitials(user?.firstName, user?.lastName, user?.email);
  const isDark = theme === 'dark';

  const handleAccount = () => navigate('/account');
  const handleLogout = () => logoutMutation.mutate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open user menu"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary/10 text-primary">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-sm font-medium text-foreground">{fullName}</p>
          {user?.email ? (
            <p className="truncate text-xs font-normal text-muted-foreground">{user.email}</p>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleAccount}>
          <UserCircle aria-hidden="true" />
          My Account
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={toggleTheme}>
          {isDark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
          {isDark ? 'Light mode' : 'Dark mode'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={handleLogout}
          className="text-destructive-text focus:text-destructive-text"
        >
          <LogOut aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
